import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import cl from 'classnames';
import { HeadsetComment, NewHeadsetComment } from 'src/models';
import { $TSFixMe } from 'src/interface';
import { RoleEnum } from 'src/security/roles';
import { HEADSETS } from 'src/constants';
import { formatDate, formatDatetime } from 'src/helper';
import { useContextData, useLanguage, usePage } from 'src/hooks';
import { useAppDispatch } from 'src/store';
import { useAccount } from 'src/services/auth';
import {
  useHeadsetById,
  addHeadsetCommentAction,
  deleteHeadsetCommentAction,
  fetchHeadsetCommentsAction,
} from 'src/services/headsets';
import {
  PageSwitcher,
  StickyTable,
  StickyTableConfig,
  useStickyCols,
} from 'src/components';
import {
  Button,
  Modal,
  Tooltip,
  FilterModal,
  FilterModalButton,
  InputWithLabel,
  DatePicker,
  CalendarDate,
  useDateRangePicker,
  FilterModalIndicator,
} from 'src/components/general';
import styles from './headsetCommentsStyle.module.scss';

const CommentCell: React.FC<{ value: $TSFixMe }> = ({ value }) => {
  const [useTooltip, setUseTooltip] = useState(false);

  return (
    <Tooltip
      text={value}
      tooltipClassName={styles.tooltip}
      arrowClassName={styles.tooltipArrow}
      withClipboard
      disabled={!useTooltip}
    >
      <div
        className={styles.comment}
        ref={(node) => {
          if (node && node.getBoundingClientRect().width > 299) {
            setUseTooltip(true);
          }
        }}
      >
        {value}
      </div>
    </Tooltip>
  );
};

export const HeadsetComments = () => {
  const { hotel: companyId, headset: headsetId } = useParams() as {
    hotel: string;
    headset: string;
  };
  const {
    access: { accessMap },
  } = usePage();
  const {
    header: { width: headerWidth },
  } = useContextData();
  const dispatch = useAppDispatch();
  const { t, currentLang } = useLanguage();
  const headset = useHeadsetById(headsetId);
  const account = useAccount();
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [removeComment, setRemoveComment] = useState<HeadsetComment | null>(
    null,
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [date, setDate] = useState<CalendarDate | null>(
    new CalendarDate().setFirstDay(),
  );
  const [commentText, setCommentText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<null | boolean>(null);
  const [isFiltered, setIsFiltered] = useState(false);

  const comments = useMemo(() => headset?.comments || [], [headset?.comments]);

  const {
    range,
    initRange,
    dateRangePicker,
    modalDateRangePicker,
    applyDRPChanges,
    resetDRPChanges,
    resetDRPToInit,
  } = useDateRangePicker({
    inputClassName: styles.datepickerInput,
    modalInputClassName: styles.datepickerModalInput,
    withoutClear: true,
    disabled: isLoading,
  });

  const rangeIndicatorText = useMemo(
    () =>
      currentLang && range.start && range.end ? (
        <>
          <span className={styles.nowrap}>{range.start.localString}</span>
          {' - '}
          <span className={styles.nowrap}>{range.end.localString}</span>
        </>
      ) : undefined,
    [range.end, range.start, currentLang],
  );

  const headsetIsLoaded = useMemo(() => Boolean(headset), [headset]);
  const isRangeChanged = useMemo(
    () =>
      range.start?.time !== initRange.start?.time ||
      range.end?.time !== initRange.end?.time,
    [
      initRange.start?.time,
      initRange.end?.time,
      range.start?.time,
      range.end?.time,
    ],
  );

  useEffect(() => {
    if (!headsetIsLoaded) {
      return;
    }

    setIsLoading(true);
    setIsFiltered(isRangeChanged);

    let start: string | undefined = undefined;
    let end: string | undefined = undefined;

    if (range.start && range.end) {
      start = range.start.format;
      end = range.end.format;
    }

    dispatch(
      fetchHeadsetCommentsAction({
        companyId,
        headsetId,
        params: {
          start,
          end,
        },
      }),
    ).then(() => {
      setIsLoading(false);
    });
  }, [dispatch, headsetIsLoaded, headsetId, companyId, range, isRangeChanged]);

  const handleOnCreate = useCallback(
    async (data: NewHeadsetComment) => {
      setIsLoading(true);

      const res = await dispatch(
        addHeadsetCommentAction({ companyId, headsetId, data }),
      );

      if (addHeadsetCommentAction.fulfilled.match(res)) {
        setShowAddModal(false);
        setDate(new CalendarDate().setFirstDay());
        setCommentText('');
      }

      setIsLoading(false);
    },
    [companyId, dispatch, headsetId],
  );

  const handleOnDelete = useCallback(
    async (commentId: string) => {
      setIsLoading(true);

      const res = await dispatch(
        deleteHeadsetCommentAction({ companyId, headsetId, commentId }),
      );

      if (deleteHeadsetCommentAction.fulfilled.match(res)) {
        setRemoveComment(null);
      }

      setIsLoading(false);
    },
    [companyId, dispatch, headsetId],
  );

  const config: StickyTableConfig<HeadsetComment>[] = useMemo(() => {
    const result: StickyTableConfig<HeadsetComment>[] = [
      {
        title: t('page.headset.comments.issueDate'),
        key: 'issueDate',
        field: 'issueDate',
        render: (value) => (
          <div className={styles.tag}>{formatDate(value)}</div>
        ),
      },
      {
        title: t('page.headset.comments.email'),
        key: 'email',
        field: 'email',
        render: (value, event) => (
          <Tooltip
            text={`ID: ${event.id}`}
            tooltipClassName={styles.tooltip}
            arrowClassName={styles.tooltipArrow}
            withClipboard
          >
            {value}
          </Tooltip>
        ),
      },
      {
        title: t('page.headset.comments.message'),
        key: 'message',
        field: 'message',
        render: (value) => <CommentCell value={value} />,
      },
      {
        title: t('page.headset.comments.createdAt'),
        key: 'createdAt',
        field: 'createdAt',
        render: (value) => (
          <div className={styles.tag}>{formatDatetime(value)}</div>
        ),
      },
    ];

    if (account?.role && [RoleEnum.admin].includes(account.role)) {
      result.push({
        title: t('page.headset.comments.action'),
        key: 'action',
        field: 'action',
        render: (_, record) => (
          <Button
            label={t('general.delete')}
            onClick={() => {
              setRemoveComment(record);
            }}
            design="pink"
            autoSize
            className={styles.deleteButton}
          />
        ),
      });
    }

    return result;
  }, [account?.role, t]);

  const {
    stickyCols,
    stickyColsFilter,
    modalStickyColsFilter,
    applySCChanges,
    resetSCChanges,
  } = useStickyCols({
    config,
    className: styles.stickyCols,
    modalClassName: styles.modalStickyCols,
  });

  const confirmText = useMemo(() => {
    if (!removeComment) {
      return null;
    }

    return t('page.headset.comments.removeConfirm', {
      message: removeComment.message,
    });
  }, [removeComment, t]);

  return (
    <div className={styles.container}>
      <FilterModal
        show={showFiltersModal}
        close={() => {
          setShowFiltersModal(false);
        }}
        onApply={() => {
          applySCChanges();
          applyDRPChanges();
          setShowFiltersModal(false);
        }}
        onCancel={() => {
          resetSCChanges();
          resetDRPChanges();
        }}
      >
        {modalDateRangePicker}
        {modalStickyColsFilter}
      </FilterModal>
      <Modal
        header={t('page.headset.comments.addingComment')}
        show={Boolean(showAddModal)}
        close={() => {
          setShowAddModal(false);
        }}
        className={styles.modal}
        footer={
          <div className={styles.modalButtons}>
            <Button
              label={t('general.cancel')}
              onClick={() => {
                setShowAddModal(false);
              }}
              design="outlineBlue"
              autoSize
              className={styles.closeBtn}
            />
            <Button
              label={t('general.add')}
              onClick={() => {
                if (date) {
                  handleOnCreate({
                    issueDate: date.format,
                    message: commentText,
                  });
                }
              }}
              design="lightBlue"
              autoSize
              disabled={!commentText}
              className={styles.actionBtn}
            />
          </div>
        }
        closeOnEsc
      >
        <div className={styles.addModalContent}>
          <DatePicker
            selectedDate={date}
            onChange={(value) => {
              setDate(value);
            }}
            withoutClear
          />
          <InputWithLabel
            label={t('page.headset.comments.comment')}
            value={commentText}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setCommentText(event.target.value);
            }}
            autoSize
            className={styles.input}
            onEnter={() => {
              if (date && commentText) {
                handleOnCreate({
                  issueDate: date.format,
                  message: commentText,
                });
              }
            }}
          />
        </div>
      </Modal>
      <Modal
        header={t('page.headset.comments.removeComment')}
        show={Boolean(removeComment)}
        close={() => {
          setRemoveComment(null);
        }}
        className={styles.modal}
        footer={
          <div className={styles.modalButtons}>
            <Button
              label={t('general.cancel')}
              onClick={() => {
                setRemoveComment(null);
              }}
              design="outlineBlue"
              autoSize
              className={styles.closeBtn}
            />
            <Button
              label={t('general.delete')}
              onClick={() => {
                if (removeComment) {
                  handleOnDelete(removeComment.id);
                }
              }}
              design="pink"
              autoSize
              className={styles.actionBtn}
            />
          </div>
        }
        closeOnEsc
      >
        <div className={styles.confirmModalContent}>{confirmText}</div>
      </Modal>
      <div
        className={styles.topSection}
        style={{ minWidth: headerWidth, maxWidth: headerWidth }}
      >
        <div
          className={cl(
            styles.pageSwitcherContainer,
            accessMap[HEADSETS].length === 1 && styles.inactive,
          )}
        >
          <PageSwitcher
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSelect}
          />
          <div className={styles.filterButtons}>
            <FilterModalButton
              setShow={setShowFiltersModal}
              disabled={isLoading}
            />
            <div
              className={cl(styles.filterButton, isLoading && styles.disabled)}
              onClick={() => {
                if (isLoading) {
                  return;
                }

                setShowAddModal(true);
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 1V17"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M1 9H17"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <FilterModalIndicator
              icon="calendar"
              text={rangeIndicatorText}
              disabled={isLoading}
            />
          </div>
        </div>
      </div>
      <div
        className={cl(styles.filters, styles.table)}
        style={{
          minWidth: headerWidth,
        }}
      >
        <div className={styles.desktopFilterButtons}>
          {dateRangePicker}
          {stickyColsFilter}
        </div>
        <Button
          label={t('page.headset.comments.addComment')}
          design="lightBlue"
          size="middle"
          className={styles.addButton}
          onClick={() => {
            setShowAddModal(true);
          }}
          disabled={Boolean(isLoading)}
        />
      </div>
      <StickyTable
        config={config}
        data={comments}
        withPaginator
        stickyCols={stickyCols}
        className={styles.table}
        isLoading={isLoading}
        isFiltered={isFiltered}
        resetFilter={resetDRPToInit}
        notFoundText={t('general.resetFilters')}
      />
    </div>
  );
};

export default HeadsetComments;
